import React from 'react';
import { Recipe } from '@/types/recipe';
import { MealPlan } from '@/types/mealPlan';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { MealNote } from '@/types/mealNote';
import { useUser } from '@/contexts/UserContext';
import RecipeCollectionStep from './steps/RecipeCollectionStep';
import MealPlanningStep from './steps/MealPlanningStep';
import ShoppingListStep from './steps/ShoppingListStep';
import OnboardingProgress from './OnboardingProgress';

interface OnboardingStepsProps {
  personalRecipeCount: number;
  totalAccessibleRecipes: number;
  personalMealPlansCount: number;
  totalMealPlansCount: number;
  personalShoppingListCount: number;
  totalShoppingListCount: number;
  latestRecipe?: Recipe;
  nextMeal?: {
    date: string;
    recipes: string[];
    mealType: string;
  } | null;
  mealPlans: MealPlan[];
  recipes: Recipe[];
  mealCompositions: MealCompositionWithRecipes[];
  mealNotes: MealNote[];
  mealDataLoading: boolean;
}

const OnboardingSteps: React.FC<OnboardingStepsProps> = ({ 
  personalRecipeCount,
  totalAccessibleRecipes,
  personalMealPlansCount,
  totalMealPlansCount,
  personalShoppingListCount,
  totalShoppingListCount,
  latestRecipe,
  nextMeal,
  mealPlans,
  recipes,
  mealCompositions,
  mealNotes,
  mealDataLoading
}) => {
  const { familyActivity } = useUser();
  
  const canPlanMeals = totalAccessibleRecipes > 0; // Can plan with any accessible recipes
  const canGenerateShoppingList = totalMealPlansCount >= 1; // Can generate from any family meal plans
  
  // Extract family activity flags
  const isActiveFamily = familyActivity?.isActiveFamily || false;
  const familyRecipeCount = familyActivity?.familyRecipeCount || 0;
  const familyMealPlanCount = familyActivity?.familyMealPlanCount || 0;

  return (
    <div className="max-w-2xl mx-auto space-y-6 px-4 sm:px-0">
      <OnboardingProgress 
        recipeCount={personalRecipeCount} // Use personal count for progress
        totalMealPlansCount={personalMealPlansCount} // Use personal count for progress
        shoppingListCount={personalShoppingListCount} // Use personal count for progress
      />
      
      <div className="space-y-3 sm:space-y-4">
        <RecipeCollectionStep 
          personalRecipeCount={personalRecipeCount}
          totalAccessibleRecipes={totalAccessibleRecipes}
          isActiveFamily={isActiveFamily}
          familyRecipeCount={familyRecipeCount}
          latestRecipe={latestRecipe} // Pass latest recipe
        />
        <MealPlanningStep
          personalMealPlansCount={personalMealPlansCount}
          totalMealPlansCount={totalMealPlansCount}
          totalAccessibleRecipes={totalAccessibleRecipes}
          isActiveFamily={isActiveFamily}
          familyMealPlanCount={familyMealPlanCount}
          nextMeal={nextMeal}
          mealPlans={mealPlans}
          recipes={recipes}
          mealCompositions={mealCompositions}
          mealNotes={mealNotes}
          mealDataLoading={mealDataLoading}
        />
        <ShoppingListStep 
          canGenerateShoppingList={canGenerateShoppingList}
          personalShoppingListCount={personalShoppingListCount}
          totalShoppingListCount={totalShoppingListCount}
          isActiveFamily={isActiveFamily}
          familyMealPlanCount={familyMealPlanCount}
        />
      </div>
    </div>
  );
};

export default OnboardingSteps;